import type { PlayerBucket } from "@pax/api-contract/accounting/v1alpha1";
import { journalLabel } from "@/features/credits/journals";
import type { CreditHistoryLine } from "@/features/credits/queries";

/** Each spendable balance by name. Moderation credits only fund moderation, so they aren't shown. */
export const BUCKET_LABELS: Record<Exclude<PlayerBucket, "moderation">, string> = {
  subscription: "Subscription",
  oneTimePurchase: "Purchased",
  daily: "Daily",
  signup: "Signup bonus",
  sponsorship: "Sponsorship",
  commission: "Creator earnings",
  compensation: "Compensation",
};

export function bucketLabel(bucket: PlayerBucket): string {
  return bucket === "moderation" ? "Moderation" : BUCKET_LABELS[bucket];
}

/**
 * One row of the balance history: back-to-back AI usage in one game folded together, or any other
 * change on its own. `lines` is newest first.
 */
export interface HistoryRow {
  key: string;
  lines: CreditHistoryLine[];
}

export interface HistoryDay {
  day: string;
  rows: HistoryRow[];
}

export type HistoryFilter = "all" | "spent" | "added";

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dayFormat = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" });
const yearDayFormat = new Intl.DateTimeFormat(undefined, {
  month: "long",
  day: "numeric",
  year: "numeric",
});

export function timeOf(recordedAt: string): string {
  return timeFormat.format(new Date(recordedAt));
}

/** "Today", "Yesterday", or the date, with the year only when it isn't this one. */
function dayOf(recordedAt: string): string {
  const when = new Date(recordedAt);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (when.toDateString() === today.toDateString()) return "Today";
  if (when.toDateString() === yesterday.toDateString()) return "Yesterday";
  return (when.getFullYear() === today.getFullYear() ? dayFormat : yearDayFormat).format(when);
}

/** Whether a line joins a row: both are AI usage in the same game. Calls with no game stand alone. */
function foldsInto(row: HistoryRow, line: CreditHistoryLine): boolean {
  const first = row.lines[0];
  const game = first?.modelCall?.game?.id;
  return (
    first?.journal === "aiSettlement" &&
    line.journal === "aiSettlement" &&
    game !== undefined &&
    game === line.modelCall?.game?.id
  );
}

export function rowTotal(row: HistoryRow): number {
  return row.lines.reduce((sum, line) => sum + line.credits, 0);
}

/** The history by day, each day's AI usage in one game folded into runs, then filtered. */
export function historyDays(lines: CreditHistoryLine[], filter: HistoryFilter): HistoryDay[] {
  const days: HistoryDay[] = [];
  for (const line of lines) {
    const day = dayOf(line.recordedAt);
    let group = days.at(-1);
    if (group?.day !== day) {
      group = { day, rows: [] };
      days.push(group);
    }
    const last = group.rows.at(-1);
    if (last && foldsInto(last, line)) last.lines.push(line);
    else group.rows.push({ key: line.entryId, lines: [line] });
  }
  return days
    .map(group => ({
      day: group.day,
      rows: group.rows.filter(row => {
        if (filter === "all") return true;
        const total = rowTotal(row);
        return filter === "spent" ? total < 0 : total > 0;
      }),
    }))
    .filter(group => group.rows.length > 0);
}

/** A row's title: the game for AI usage, or what kind of change it was. */
export function rowTitle(row: HistoryRow): string {
  const first = row.lines[0];
  if (!first) return "";
  if (first.journal === "aiSettlement") return first.modelCall?.game?.title ?? "AI usage";
  return journalLabel(first.journal);
}

/** The line under a row's title: for a run of AI calls, how many and over what stretch of time. */
export function rowDetail(row: HistoryRow): string {
  const first = row.lines[0];
  const last = row.lines.at(-1);
  if (!first || !last) return "";
  if (first.journal !== "aiSettlement") return timeOf(first.recordedAt);
  if (row.lines.length === 1) return `AI usage · ${timeOf(first.recordedAt)}`;
  return `AI usage · ${row.lines.length} calls · ${timeOf(last.recordedAt)}–${timeOf(first.recordedAt)}`;
}

/** The agent that made an AI call, by name and version. */
export function agentName(line: CreditHistoryLine): string | null {
  const agent = line.modelCall?.agent;
  return agent ? `${agent.name} v${agent.version}` : null;
}
